import { forwardRef, useMemo, type ReactNode } from "react";
import { Chart, useChartTheme, resolveChartColor, usePrefersReducedMotion, commonStyles, commonTooltip, commonAxis, commonLegend, type ChartHandle, type ChartProps } from "./Chart";

// Stage 6: local replacement for @datasirpi/ui's <BarChart>. Ported
// letter-for-letter from the vendor's compiled bundle, incl. the
// per-bar entry-stagger animation delay and horizontal/vertical
// orientation axis-swap.
export type BarOrientation = "vertical" | "horizontal";

export interface BarSeries<Row> {
  name: string;
  value: (row: Row) => number;
  color?: string;
  stack?: string;
}

export interface BarChartProps<Row> extends Omit<ChartProps, "option" | "tabularData"> {
  data: readonly Row[];
  xKey: (row: Row) => string | number;
  series: ReadonlyArray<BarSeries<Row>>;
  orientation?: BarOrientation;
  /**
   * Width reserved for category labels on a horizontal chart, in px.
   * Defaults to 80, which is right for a full-width card and too narrow for
   * a column: ECharts right-aligns these labels against the axis, so a label
   * wider than the gutter overflows LEFT and is clipped by the canvas edge —
   * it reads as a name with its beginning chopped off ("windows-security"
   * shown as "ows-security"), which is worse than an ellipsis. Widen this
   * instead of truncating the data when the chart sits in a narrow slot.
   */
  categoryGutter?: number;
  showLegend?: boolean;
  showTooltip?: boolean;
  yAxisFormat?: (value: number) => string;
  tableCaption?: string;
}

function BarChartInner<Row>(
  { data, xKey, series, orientation = "vertical", categoryGutter = 80, showLegend, showTooltip = true, yAxisFormat, tableCaption, ...rest }: BarChartProps<Row>,
  ref: React.ForwardedRef<ChartHandle>
) {
  const theme = useChartTheme();
  const reducedMotion = usePrefersReducedMotion();

  const option = useMemo(() => {
    const categories = data.map(xKey);
    const categoryAxis = { type: "category", data: categories, ...commonAxis(theme) };
    const valueAxis = {
      type: "value",
      ...commonAxis(theme),
      axisLabel: { ...commonAxis(theme).axisLabel, ...(yAxisFormat ? { formatter: yAxisFormat } : {}) },
    };
    const horizontal = orientation === "horizontal";
    const isLegendShown = showLegend ?? series.length > 1;
    return {
      ...commonStyles(theme, { reducedMotion }),
      grid: { top: 24, right: 16, bottom: isLegendShown ? 56 : horizontal ? 32 : 24, left: horizontal ? categoryGutter : 48 },
      tooltip: showTooltip ? { trigger: "axis", axisPointer: { type: "shadow" }, ...commonTooltip(theme) } : { show: false },
      legend: isLegendShown ? { show: true, data: series.map((s) => s.name), ...commonLegend(theme) } : { show: false },
      xAxis: horizontal ? valueAxis : categoryAxis,
      yAxis: horizontal ? categoryAxis : valueAxis,
      series: series.map((s, i) => ({
        name: s.name,
        type: "bar",
        stack: s.stack,
        data: data.map((row) => s.value(row)),
        itemStyle: {
          color: resolveChartColor(s.color, theme.palette[i % theme.palette.length]),
          borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
        },
        barMaxWidth: 36,
        animation: !reducedMotion,
        // Stage 6, motion gap: was 800ms, matching Chart.tsx's own
        // commonStyles() fix — design.md's Standard band tops out at 250ms.
        animationDuration: reducedMotion ? 0 : 250,
        animationDurationUpdate: reducedMotion ? 0 : 300,
        animationEasing: "cubicOut",
        // Was idx * 40 + i * 80: an 8-10 category chart staggered bars over
        // up to ~280-360ms on top of each bar's own duration, compounding
        // well past the Standard band. Halved multipliers keep total stagger
        // under ~150-200ms for a realistic category count.
        animationDelay: reducedMotion ? 0 : (idx: number) => idx * 20 + i * 40,
      })),
    };
  }, [data, xKey, series, orientation, categoryGutter, theme, reducedMotion, showLegend, showTooltip, yAxisFormat]);

  const tabularData = useMemo(
    () => ({
      ...(tableCaption ? { caption: tableCaption } : {}),
      headers: ["category", ...series.map((s) => s.name)] as ReactNode[],
      rows: data.map((row) => [String(xKey(row)), ...series.map((s) => String(s.value(row)))]),
    }),
    [data, xKey, series, tableCaption]
  );

  return <Chart ref={ref} option={option} tabularData={tabularData} {...rest} />;
}

export const BarChart = forwardRef(BarChartInner) as <Row>(
  props: BarChartProps<Row> & { ref?: React.ForwardedRef<ChartHandle> }
) => ReturnType<typeof BarChartInner>;
